import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { CURRENCIES } from "../../constants/currencies";
import { useUserEntitlements } from "../../hooks/useUserEntitlements";
import { LimitWarning, LimitIndicator } from "../governance";
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  BarChart3,
  MapPin,
  Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const SubsidiaryManager = () => {
  const { canCreate, getLimit } = useUserEntitlements();
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    currency: "USD",
    parent_id: "",
    is_active: true
  });

  useEffect(() => {
    fetchSubsidiaries();
  }, []);

  const fetchSubsidiaries = async () => {
    try {
      // Use company_only=true to get only this user's company subsidiaries (optimized)
      const response = await axios.get(`${API}/subsidiaries?company_only=true`);
      setSubsidiaries(response.data);
    } catch (error) {
      toast.error("Failed to load subsidiaries");
      console.error("Error fetching subsidiaries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/subsidiaries`, formData);
      toast.success("Subsidiary created successfully");
      setCreateDialogOpen(false);
      resetForm();
      fetchSubsidiaries();
    } catch (error) {
      console.error('❌ Error creating subsidiary:', error);
      
      // Check if this is a limit exceeded error (403)
      if (error.response && error.response.status === 403) {
        const limit = getLimit('subsidiaries');
        toast.error(
          <div>
            <p className="font-bold">Subsidiary Limit Reached!</p>
            <p className="text-sm">You've used {limit?.current || 0} of {limit?.total || 0} subsidiaries.</p>
            <p className="text-sm mt-2">Upgrade to Business ($149/mo) for 5 subsidiaries, or Enterprise ($399/mo) for unlimited subsidiaries.</p>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(error.response?.data?.detail || "Failed to create subsidiary");
      }
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/subsidiaries/${selectedSubsidiary.id}`, formData);
      toast.success("Subsidiary updated successfully");
      setEditDialogOpen(false);
      resetForm();
      fetchSubsidiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subsidiary");
    }
  };

  const handleDelete = async (subsidiary) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${subsidiary.name}"?\n\nThis will remove the subsidiary and may affect associated business plans.`
    );

    if (confirmed) {
      try {
        await axios.delete(`${API}/subsidiaries/${subsidiary.id}`);
        toast.success("Subsidiary deleted successfully");
        fetchSubsidiaries();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to delete subsidiary");
        console.error("Error deleting subsidiary:", error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      description: "",
      currency: "USD",
      parent_id: "",
      is_active: true
    });
    setSelectedSubsidiary(null);
  };

  const openEditDialog = (subsidiary) => {
    setSelectedSubsidiary(subsidiary);
    setFormData({
      name: subsidiary.name,
      code: subsidiary.code,
      description: subsidiary.description || "",
      currency: subsidiary.currency,
      parent_id: subsidiary.parent_id || "",
      is_active: subsidiary.is_active
    });
    setEditDialogOpen(true);
  };

  const buildHierarchy = (subsidiaries, parentId = null) => {
    return subsidiaries
      .filter(sub => sub.parent_id === parentId)
      .map(sub => ({
        ...sub,
        children: buildHierarchy(subsidiaries, sub.id)
      }));
  };

  const renderSubsidiaryTree = (subsidiaries, level = 0) => {
    return subsidiaries.map(subsidiary => (
      <div key={subsidiary.id} className={`mb-4 ${level > 0 ? 'ml-8' : ''}`}>
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Building2 className="h-5 w-5 text-blue-600" />
                <div>
                  <CardTitle className="text-lg">{subsidiary.name}</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    Code: {subsidiary.code} • {subsidiary.currency}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant={subsidiary.is_active ? "default" : "secondary"}>
                  {subsidiary.is_active ? "Active" : "Inactive"}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditDialog(subsidiary)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(subsidiary)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {subsidiary.description && (
              <p className="text-gray-600 mb-3">{subsidiary.description}</p>
            )}
            
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Users className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                <p className="text-sm font-medium">0 Users</p>
                <p className="text-xs text-gray-500">Assigned</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <BarChart3 className="h-5 w-5 mx-auto mb-1 text-green-600" />
                <p className="text-sm font-medium">0 Plans</p>
                <p className="text-xs text-gray-500">Business Plans</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <MapPin className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                <p className="text-sm font-medium">Level {level + 1}</p>
                <p className="text-xs text-gray-500">Hierarchy</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {subsidiary.children && subsidiary.children.length > 0 && 
          renderSubsidiaryTree(subsidiary.children, level + 1)
        }
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subsidiaries...</p>
        </div>
      </div>
    );
  }

  const hierarchicalSubsidiaries = buildHierarchy(subsidiaries);

  return (
    <div className="space-y-6">
      {/* Governance: Limit Warning */}
      <LimitWarning limitType="subsidiaries" />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subsidiaries</h1>
          <p className="text-gray-600 mt-1">
            Manage your company's subsidiaries and organizational structure
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Governance: Compact Limit Display */}
          <LimitIndicator limitType="subsidiaries" compact />
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={(e) => {
                  if (!canCreate('subsidiaries')) {
                    e.preventDefault();
                    // Check if Starter tier
                    const userTier = entitlements?.tier?.toLowerCase();
                    if (userTier === 'starter') {
                      // Show CTA
                      toast.error(
                        <div className="space-y-2">
                          <p className="font-bold">Subsidiary Limit Reached</p>
                          <p className="text-sm">Upgrade to Business or buy an add-on to create subsidiaries.</p>
                          <div className="flex gap-2 mt-2">
                            <button 
                              onClick={() => window.location.href = '/subscription'}
                              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                            >
                              View Subscription
                            </button>
                            <button 
                              onClick={() => window.location.href = '/add-ons'}
                              className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                            >
                              Browse Add-ons
                            </button>
                          </div>
                        </div>,
                        { duration: 10000 }
                      );
                    } else {
                      const limit = getLimit('subsidiaries');
                      toast.error(`You've reached your subsidiary limit (${limit?.total}). Please upgrade your subscription.`);
                    }
                  }
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Subsidiary
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Subsidiary</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label htmlFor="name">Subsidiary Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter subsidiary name"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="code">Subsidiary Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., SUB001"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="parent">Parent Subsidiary</Label>
                <Select value={formData.parent_id || "none"} onValueChange={(value) => setFormData({...formData, parent_id: value === "none" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select parent (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Parent (Top Level)</SelectItem>
                    {subsidiaries.map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {curr.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCreateDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Subsidiary
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Subsidiary Tree */}
      <div className="space-y-4">
        {hierarchicalSubsidiaries.length > 0 ? (
          renderSubsidiaryTree(hierarchicalSubsidiaries)
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No subsidiaries found</h3>
              <p className="text-gray-500 mb-4">
                Create your first subsidiary to start organizing your business structure
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Subsidiary
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Subsidiary</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Subsidiary Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-code">Subsidiary Code *</Label>
              <Input
                id="edit-code"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-parent">Parent Subsidiary</Label>
              <Select value={formData.parent_id || "none"} onValueChange={(value) => setFormData({...formData, parent_id: value === "none" ? "" : value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select parent (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Parent (Top Level)</SelectItem>
                  {subsidiaries
                    .filter(sub => sub.id !== selectedSubsidiary?.id)
                    .map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="edit-currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((curr) => (
                    <SelectItem key={curr.value} value={curr.value}>
                      {curr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                Update Subsidiary
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubsidiaryManager;